import { useEffect, useState } from "react";
import { ChevronRight, Palette, Pencil, X, Check, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";

import { getCompanyReportThemeApi, updateCompanyReportThemeApi } from "../../api/api";
import { ReportTheme } from "../../api/models";
import sharedStyles from "./UserSettings.module.css";
import styles from "./ReportThemeSettings.module.css";

// Mirrors DEFAULT_THEME in app/backend/end_report/dynamic_docx_builder.py -
// keep in sync if that default ever changes. Only used so the form always
// renders a complete shape, even before the first GET resolves. Deliberately
// unbranded: this is the starting point every company sees, so it must not be
// some other company's colours.
const DEFAULT_REPORT_THEME: ReportTheme = {
    accent: "44546A",
    heading_color: "2F5496",
    title_color: "1F3864",
    body_font: "Calibri",
    heading_font: "Calibri",
    logo_base64: "",
    footer_labels: { management: "", system: "" },
    chart: {
        client: "#4472C4",
        ref_line: "#70AD47",
        ref_band: "#E2EFDA",
        ref_bar: "#A9D08E",
        pie: ["#4472C4", "#ED7D31", "#A5A5A5", "#5B9BD5", "#70AD47", "#FFC000", "#264478"]
    }
};

const FONT_OPTIONS = ["Calibri", "Arial", "Verdana", "Times New Roman", "Georgia"];

function normHex(value: string, fallback: string): string {
    const v = value.trim().replace(/^#/, "");
    return /^[0-9A-Fa-f]{6}$/.test(v) ? v.toUpperCase() : fallback;
}

interface ColorFieldProps {
    label: string;
    value: string; // hex, no '#'
    onChange: (hex: string) => void;
    disabled: boolean;
}

const ColorField = ({ label, value, onChange, disabled }: ColorFieldProps) => (
    <div className={styles.colorField}>
        <label className={sharedStyles.accountLabel}>{label}</label>
        <div className={styles.colorRow}>
            <input
                type="color"
                className={styles.colorSwatch}
                value={`#${value}`}
                disabled={disabled}
                onChange={e => onChange(e.target.value.replace("#", "").toUpperCase())}
            />
            <input
                type="text"
                className={styles.colorHexInput}
                value={value}
                disabled={disabled}
                onChange={e => onChange(normHex(e.target.value, value))}
            />
        </div>
    </div>
);

interface ReportThemeSettingsProps {
    getIdToken: () => Promise<string | undefined>;
}

export const ReportThemeSettings = ({ getIdToken }: ReportThemeSettingsProps) => {
    const { t } = useTranslation();

    const [theme, setTheme] = useState<ReportTheme>(DEFAULT_REPORT_THEME);
    const [draft, setDraft] = useState<ReportTheme>(DEFAULT_REPORT_THEME);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const idToken = await getIdToken();
                const loaded = await getCompanyReportThemeApi(idToken);
                if (cancelled) return;
                const merged: ReportTheme = {
                    ...DEFAULT_REPORT_THEME,
                    ...loaded,
                    footer_labels: { ...DEFAULT_REPORT_THEME.footer_labels, ...(loaded?.footer_labels || {}) },
                    chart: { ...DEFAULT_REPORT_THEME.chart, ...(loaded?.chart || {}) }
                };
                setTheme(merged);
                setDraft(merged);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : String(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startEditing = () => {
        setDraft(theme);
        setEditing(true);
        setError(null);
    };

    const cancelEditing = () => {
        setDraft(theme);
        setEditing(false);
        setError(null);
    };

    const saveTheme = async () => {
        setSaving(true);
        setError(null);
        try {
            const idToken = await getIdToken();
            const saved = await updateCompanyReportThemeApi(draft, idToken);
            setTheme(saved);
            setDraft(saved);
            setEditing(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const updateDraft = (patch: Partial<ReportTheme>) => setDraft(prev => ({ ...prev, ...patch }));
    const updateChart = (patch: Partial<ReportTheme["chart"]>) =>
        setDraft(prev => ({ ...prev, chart: { ...prev.chart, ...patch } }));
    const updatePieColor = (index: number, hex: string) =>
        setDraft(prev => {
            const pie = [...prev.chart.pie];
            pie[index] = `#${hex}`;
            return { ...prev, chart: { ...prev.chart, pie } };
        });
    const updateFooterLabel = (key: "management" | "system", value: string) =>
        setDraft(prev => ({ ...prev, footer_labels: { ...prev.footer_labels, [key]: value } }));

    const onLogoUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === "string" ? reader.result : "";
            const base64 = result.split(",")[1] || "";
            updateDraft({ logo_base64: base64 });
        };
        reader.readAsDataURL(file);
    };

    const view = editing ? draft : theme;
    const disabled = !editing || saving;

    return (
        <div className={sharedStyles.accountSection}>
            <details className={sharedStyles.schedulingRulesDisclosure}>
                <summary className={sharedStyles.schedulingRulesSummary}>
                    <ChevronRight size={16} className={sharedStyles.disclosureChevron} />
                    <Palette size={18} className={sharedStyles.accountSectionIcon} />
                    <span className={sharedStyles.schedulingRulesSummaryTitle}>
                        {t("settingsPage.company.reportTheme.title", { defaultValue: "Report Theme" })}
                    </span>
                </summary>

                <div className={sharedStyles.schedulingRulesBody}>
                    <div className={sharedStyles.schedulingRulesActions}>
                        {!editing ? (
                            <button className={sharedStyles.editButton} onClick={startEditing}>
                                <Pencil size={16} />
                                {t("settingsPage.account.edit", { defaultValue: "Edit" })}
                            </button>
                        ) : (
                            <div className={sharedStyles.editActions}>
                                <button className={sharedStyles.cancelButton} onClick={cancelEditing} disabled={saving}>
                                    <X size={16} />
                                    {t("settingsPage.account.cancel", { defaultValue: "Cancel" })}
                                </button>
                                <button className={sharedStyles.saveButton} onClick={saveTheme} disabled={saving}>
                                    <Check size={16} />
                                    {saving
                                        ? t("settingsPage.account.saving", { defaultValue: "Saving…" })
                                        : t("settingsPage.account.save", { defaultValue: "Save" })}
                                </button>
                            </div>
                        )}
                    </div>

                    <p className={sharedStyles.sharedDocsDescription}>
                        {t("settingsPage.company.reportTheme.description", {
                            defaultValue: "Colors, fonts, and logo used on every report this company generates."
                        })}
                    </p>

                    {error && <div className={sharedStyles.errorState}>{error}</div>}
                    {loading && <div className={sharedStyles.loadingState}>{t("settingsPage.loading", { defaultValue: "Loading…" })}</div>}

                    {!loading && (
                        <div className={styles.layout}>
                            <div className={styles.controls}>
                                <ColorField
                                    label={t("settingsPage.company.reportTheme.accent", { defaultValue: "Accent" })}
                                    value={view.accent}
                                    disabled={disabled}
                                    onChange={hex => updateDraft({ accent: hex })}
                                />
                                <ColorField
                                    label={t("settingsPage.company.reportTheme.headingColor", { defaultValue: "Heading color" })}
                                    value={view.heading_color}
                                    disabled={disabled}
                                    onChange={hex => updateDraft({ heading_color: hex })}
                                />
                                <ColorField
                                    label={t("settingsPage.company.reportTheme.titleColor", { defaultValue: "Title color" })}
                                    value={view.title_color}
                                    disabled={disabled}
                                    onChange={hex => updateDraft({ title_color: hex })}
                                />

                                <label className={sharedStyles.accountLabel}>
                                    {t("settingsPage.company.reportTheme.bodyFont", { defaultValue: "Body font" })}
                                </label>
                                <select
                                    className={styles.fontSelect}
                                    value={view.body_font}
                                    disabled={disabled}
                                    onChange={e => updateDraft({ body_font: e.target.value })}
                                >
                                    {FONT_OPTIONS.map(f => (
                                        <option key={f} value={f}>
                                            {f}
                                        </option>
                                    ))}
                                </select>

                                <label className={sharedStyles.accountLabel}>
                                    {t("settingsPage.company.reportTheme.headingFont", { defaultValue: "Heading font" })}
                                </label>
                                <select
                                    className={styles.fontSelect}
                                    value={view.heading_font}
                                    disabled={disabled}
                                    onChange={e => updateDraft({ heading_font: e.target.value })}
                                >
                                    {FONT_OPTIONS.map(f => (
                                        <option key={f} value={f}>
                                            {f}
                                        </option>
                                    ))}
                                </select>

                                <label className={sharedStyles.accountLabel}>
                                    {t("settingsPage.company.reportTheme.logo", { defaultValue: "Logo" })}
                                </label>
                                <label className={styles.logoUpload}>
                                    <Upload size={14} />
                                    {t("settingsPage.company.reportTheme.uploadLogo", { defaultValue: "Upload logo" })}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        disabled={disabled}
                                        style={{ display: "none" }}
                                        onChange={e => e.target.files && e.target.files[0] && onLogoUpload(e.target.files[0])}
                                    />
                                </label>

                                <label className={sharedStyles.accountLabel}>
                                    {t("settingsPage.company.reportTheme.chartColors", { defaultValue: "Chart colors" })}
                                </label>
                                <ColorField
                                    label={t("settingsPage.company.reportTheme.chartClient", { defaultValue: "Primary series" })}
                                    value={normHex(view.chart.client, "E5157F")}
                                    disabled={disabled}
                                    onChange={hex => updateChart({ client: `#${hex}` })}
                                />
                                <ColorField
                                    label={t("settingsPage.company.reportTheme.chartRefBar", { defaultValue: "Reference bar" })}
                                    value={normHex(view.chart.ref_bar, "B6D7A8")}
                                    disabled={disabled}
                                    onChange={hex => updateChart({ ref_bar: `#${hex}` })}
                                />

                                <label className={sharedStyles.accountLabel}>
                                    {t("settingsPage.company.reportTheme.pieColors", { defaultValue: "Pie slice colors" })}
                                </label>
                                <div className={styles.pieRow}>
                                    {view.chart.pie.map((color, i) => (
                                        <input
                                            key={i}
                                            type="color"
                                            className={styles.colorSwatch}
                                            value={`#${normHex(color, "808080")}`}
                                            disabled={disabled}
                                            onChange={e => updatePieColor(i, e.target.value.replace("#", "").toUpperCase())}
                                        />
                                    ))}
                                </div>

                                <label className={sharedStyles.accountLabel}>
                                    {t("settingsPage.company.reportTheme.footerLabels", { defaultValue: "Footer labels" })}
                                </label>
                                <input
                                    type="text"
                                    className={styles.textInput}
                                    value={view.footer_labels.management}
                                    disabled={disabled}
                                    onChange={e => updateFooterLabel("management", e.target.value)}
                                />
                                <input
                                    type="text"
                                    className={styles.textInput}
                                    value={view.footer_labels.system}
                                    disabled={disabled}
                                    onChange={e => updateFooterLabel("system", e.target.value)}
                                />
                            </div>

                            <div className={styles.preview}>
                                <div className={styles.previewLogoRow}>
                                    {view.logo_base64 && <img src={`data:image/png;base64,${view.logo_base64}`} alt="" />}
                                </div>
                                <div className={styles.previewTitle} style={{ color: `#${view.title_color}`, fontFamily: view.heading_font }}>
                                    {t("settingsPage.company.reportTheme.previewTitle", { defaultValue: "Sample Report" })}
                                </div>
                                <div
                                    className={styles.previewHeading}
                                    style={{ color: `#${view.heading_color}`, fontFamily: view.heading_font }}
                                >
                                    {t("settingsPage.company.reportTheme.previewSection", { defaultValue: "Section heading" })}
                                </div>
                                <table className={styles.previewTable} style={{ fontFamily: view.body_font }}>
                                    <thead>
                                        <tr>
                                            <th style={{ background: `#${view.accent}` }}>A</th>
                                            <th style={{ background: `#${view.accent}` }}>B</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>1</td>
                                            <td>2</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div className={styles.previewFooterRow}>
                                    <span className={styles.previewFooterChip} style={{ background: `#${view.accent}` }}>
                                        {view.footer_labels.management}
                                    </span>
                                    <span className={styles.previewFooterChip} style={{ background: `#${view.accent}` }}>
                                        {view.footer_labels.system}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </details>
        </div>
    );
};
